import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Settings as SettingsIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

import BreathingCue from '@/components/BreathingCue';
import TypingCanvas from '@/components/TypingCanvas';
import { createSession, fetchModes, reflectOnSession } from '@/lib/api';
import { getAnonId } from '@/lib/storage';
import EXHALE from '@/lib/testIds';

export default function Session({ settings, onOpenSettings }) {
  const { mode } = useParams();
  const navigate = useNavigate();

  const [modes, setModes] = useState([]);
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('typing'); // typing | finished
  const [savedSession, setSavedSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wantReflection, setWantReflection] = useState(settings.reflections);

  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    let active = true;
    fetchModes().then((data) => { if (active) setModes(data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setWantReflection(settings.reflections);
  }, [settings.reflections]);

  const meta = useMemo(
    () => modes.find((m) => m.id === mode) || null,
    [modes, mode],
  );

  const wordCount = useMemo(
    () => text.trim().split(/\s+/).filter(Boolean).length,
    [text],
  );

  const handleFinish = async () => {
    if (saving) return;
    if (!text.trim()) {
      toast('nothing to save yet — just a little more', { duration: 2200 });
      return;
    }
    setSaving(true);
    try {
      const session = await createSession({
        anon_id: getAnonId(),
        mode: meta?.id || 'free',
        content: text,
        duration_seconds: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
        want_reflection: wantReflection,
      });
      setSavedSession(session);
      setPhase('finished');
    } catch (err) {
      toast('something didn\'t save — your words are still here');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateReflection = async () => {
    if (!savedSession) return;
    try {
      const updated = await reflectOnSession(savedSession.id, getAnonId());
      setSavedSession(updated);
    } catch (_) {
      toast('couldn\'t fetch a reflection — that\'s okay');
    }
  };

  const startNew = () => {
    setText('');
    setSavedSession(null);
    startedAtRef.current = Date.now();
    setPhase('typing');
  };

  return (
    <section
      data-testid={EXHALE.sessionRoot}
      className="relative min-h-screen px-6 sm:px-12 pb-24 pt-8 sm:pt-12"
    >
      <BreathingCue active={settings.breathing} dimmed={phase !== 'typing'} />

      {/* Quiet top row */}
      <div className="relative z-10 mx-auto flex max-w-2xl items-center justify-between text-sm">
        <Link
          to="/"
          data-testid={EXHALE.goHomeBtn}
          className="quiet-link inline-flex items-center gap-2 opacity-70 hover:opacity-100"
        >
          <ArrowLeft size={14} strokeWidth={1.25} />
          close
        </Link>
        <div className="flex items-center gap-6">
          <span data-testid={EXHALE.wordCount} className="eyebrow opacity-80">
            {wordCount === 0 ? 'no words yet' : `${wordCount} ${wordCount === 1 ? 'word' : 'words'}`}
          </span>
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid={EXHALE.settingsBtn}
            className="opacity-60 hover:opacity-100 transition-opacity duration-500"
            aria-label="Settings"
          >
            <SettingsIcon size={16} strokeWidth={1.25} />
          </button>
        </div>
      </div>

      <div className="relative z-10 mx-auto mt-10 sm:mt-14 max-w-2xl">
        <AnimatePresence mode="wait">
          {phase === 'typing' && (
            <motion.div
              key="typing"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {meta && (
                <>
                  <p className="eyebrow">{meta.name.toLowerCase()}</p>
                  <h1 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight leading-tight">
                    {meta.prompt || 'Type whatever wants out.'}
                  </h1>
                  <p className="mt-3 text-base text-muted-foreground">
                    {meta.subtitle}
                  </p>
                </>
              )}

              <div className="mt-10 sm:mt-14">
                <TypingCanvas
                  value={text}
                  onChange={setText}
                  placeholder="start anywhere…"
                />
              </div>

              <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <label
                  htmlFor="reflect-toggle"
                  className="flex items-center gap-3 text-sm text-muted-foreground select-none cursor-pointer"
                >
                  <input
                    id="reflect-toggle"
                    type="checkbox"
                    data-testid={EXHALE.reflectToggle}
                    checked={wantReflection}
                    onChange={(e) => setWantReflection(e.target.checked)}
                    className="h-4 w-4 rounded-sm border border-border accent-sage"
                  />
                  one quiet sentence when I'm done
                </label>

                <button
                  type="button"
                  data-testid={EXHALE.finishBtn}
                  onClick={handleFinish}
                  disabled={saving}
                  className="quiet-link inline-flex items-center gap-2 self-start sm:self-auto text-base disabled:opacity-50"
                >
                  {saving ? 'saving…' : (
                    <>
                      finish & exhale
                      <Check size={16} strokeWidth={1.25} />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {phase === 'finished' && savedSession && (
            <motion.div
              key="finished"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="py-8"
            >
              <p className="eyebrow">done</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-light tracking-tight leading-tight">
                That's enough for now.
              </h2>

              {wantReflection && (
                <div className="mt-10">
                  {savedSession.reflection ? (
                    <motion.p
                      data-testid={EXHALE.reflectionText}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 1.2 }}
                      className="font-serif text-xl sm:text-2xl italic leading-relaxed text-foreground/85"
                    >
                      “{savedSession.reflection}”
                    </motion.p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleGenerateReflection}
                      className="quiet-link text-sm opacity-70 hover:opacity-100"
                    >
                      ask for a quiet sentence
                    </button>
                  )}
                </div>
              )}

              <div className="mt-14 flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-10">
                <button
                  type="button"
                  data-testid={EXHALE.newSessionBtn}
                  onClick={startNew}
                  className="quiet-link text-base"
                >
                  begin another exhale
                </button>
                <Link
                  to="/history"
                  className="quiet-link text-base opacity-70 hover:opacity-100"
                >
                  past exhales
                </Link>
                <Link
                  to="/"
                  data-testid={EXHALE.goHomeBtn}
                  className="quiet-link text-base opacity-70 hover:opacity-100"
                >
                  close
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
